import * as React from 'react';
import { useWindowSize } from 'react-use';
import { Box, Grid, TextField, Typography, useTheme } from '@mui/material';

import { CrystalButton, EnhancedTable } from 'components';
import { employeesTableColumns } from 'configs';
import { convertEmployeesToTableData } from 'utils';
import { useEmployeesTableRows } from './employees.hook';
import { useAppDispatch, useAppSelector } from 'store/store.hook';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { EmployeeCreateDialog, EmployeeUpdateDialog, UserAssignedBuildingsDialog } from 'dialogs/user';
import { updateLayout } from 'store/slices/layout.slice';

type State = {
  employeeId: number | null;
  visibleUserAssignedBuildingsDialog: boolean;
  visibleEmployeeUpdateDialog: boolean;
};

export const EmployeesPage = () => {
  const { width } = useWindowSize();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { isAdmin } = useAppSelector(selectCurrentUserInfo);
  const [searchKeyword, setSearchKeyword] = React.useState<string>('');
  const [{ employeeId, visibleUserAssignedBuildingsDialog, visibleEmployeeUpdateDialog }, setState] =
    React.useState<State>({
      employeeId: null,
      visibleUserAssignedBuildingsDialog: false,
      visibleEmployeeUpdateDialog: false,
    });
  const [loading, employees] = useEmployeesTableRows({
    searchKeyword,
    setState,
  });
  const [visibleEmployeeCreateDialog, setVisibleEmployeeCreateDialog] = React.useState<boolean>(false);

  React.useEffect(() => {
    dispatch(updateLayout({ headerTitle: 'Employees' }));
  }, []);

  const openAddEmployee = () => {
    setVisibleEmployeeCreateDialog(true);
  };

  const columns = employeesTableColumns;
  const columnKeys = columns.map((item) => item.id);

  return (
    <>
      <Box sx={{ padding: 3 }}>
        <Grid container rowSpacing={5} columnSpacing={3}>
          <Grid
            item
            xs={12}
            container
            spacing={2}
            alignItems={width < theme.breakpoints.values.md ? 'center' : 'flex-start'}
          >
            <Grid item sx={{ flex: 1 }}>
              <TextField
                label="Search by User"
                autoComplete="off"
                size="small"
                fullWidth
                InputProps={{ sx: { bgcolor: 'white' } }}
                value={searchKeyword}
                onChange={(event) => setSearchKeyword(event.target.value)}
                sx={{ maxWidth: width < theme.breakpoints.values.md ? 345 : 480 }}
              />
            </Grid>

            <Grid item>
              <CrystalButton onClick={openAddEmployee} />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 500 }}>
              Employees are individuals that service zones. <br /> They use the 4 digit Employee Code to log a service
              record.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <EnhancedTable
              config={{ toolbar: false, rowCheckable: false }}
              loading={loading}
              loadingLabel={'Loading employees'}
              noDataLabel={'No employee'}
              data={employees}
              convertFunctionForDataToTableData={convertEmployeesToTableData(isAdmin)}
              headColumns={columns}
              columnKeys={columnKeys}
              styles={{ tableStyle: { minWidth: 0 } }}
              initialRowsPerPage={10}
              visiblePagination
              rowActions={{}}
            />
          </Grid>
        </Grid>
      </Box>

      <EmployeeCreateDialog visible={visibleEmployeeCreateDialog} setVisible={setVisibleEmployeeCreateDialog} />

      {typeof employeeId === 'number' && (
        <>
          <UserAssignedBuildingsDialog
            visible={visibleUserAssignedBuildingsDialog}
            setVisible={(visibleUserAssignedBuildingsDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleUserAssignedBuildingsDialog }))
            }
            userId={employeeId}
          />

          <EmployeeUpdateDialog
            visible={visibleEmployeeUpdateDialog}
            setVisible={(visibleEmployeeUpdateDialog: boolean) =>
              setState((prev) => ({ ...prev, visibleEmployeeUpdateDialog }))
            }
            employeeId={employeeId}
          />
        </>
      )}
    </>
  );
};
